import * as React from 'react';
import './RegisterPage.css';
import { useDispatch } from 'react-redux';
import { register } from './actions';

import Logo from '../../assets/Logo.png';

import Input from '../../components/Input/Input';
import Button from '../../components/Button/Button';

import { Controller, useForm } from 'react-hook-form';
import Text from '../../components/Text/Text';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FormItem from '../../components/FormItem/FormItem';
import { useTranslation } from 'react-i18next';
import { CrownOutlined } from '@ant-design/icons';

type FormValues = {
  email: string;
  confirm: string;
  password: string;
  referralCode: string;
};

const RegisterPage = React.memo(() => {
  const { t } = useTranslation('translation');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refFromUrl = searchParams.get('ref') ?? '';

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      referralCode: refFromUrl,
    },
  });

  const onSubmit = handleSubmit(({ email, password, referralCode }) => {
    dispatch(
      register({
        email,
        password,
        referralCode: referralCode?.trim() || undefined,
      }),
    );
  });

  const handleRedirectHome = React.useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleSignInClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      handleRedirectHome();
    },
    [handleRedirectHome],
  );

  return (
    <div className="AuthPage">
      <div className="AuthCard">
        <span className="PageHero__eyebrow RegisterPage__eyebrow">
          <CrownOutlined /> Fish Hunter
        </span>
        <img className="AuthCard__logo" src={Logo} alt="Fish Hunter" onClick={handleRedirectHome} />
        <Text className="AuthCard__title" size={5} bold>
          {t('sign_up')}
        </Text>
        <form className="AuthCard__form" onSubmit={onSubmit}>
          <FormItem error={errors.email?.message}>
            <Controller
              name="email"
              control={control}
              rules={{
                required: t('required'),
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: t('entered_value_does_not_match_email_format'),
                },
              }}
              render={({ field }) => (
                <Input
                  placeholder="Email"
                  autoComplete="off"
                  aria-invalid={errors.email?.message ? true : false}
                  {...field}
                />
              )}
            />
          </FormItem>

          <FormItem error={errors.password?.message}>
            <Controller
              name="password"
              control={control}
              rules={{
                required: t('required'),
              }}
              render={({ field }) => (
                <Input
                  placeholder={t('password')}
                  autoComplete="off"
                  type="password"
                  aria-invalid={errors.password?.message ? true : false}
                  {...field}
                />
              )}
            />
          </FormItem>

          <FormItem error={errors.confirm?.message}>
            <Controller
              name="confirm"
              control={control}
              rules={{
                required: t('required'),
                validate: (value) => {
                  if (watch('password') !== value) {
                    return t('passwords_do_not_match');
                  }
                },
              }}
              render={({ field }) => (
                <Input
                  placeholder={t('confirm_password')}
                  autoComplete="off"
                  type="password"
                  aria-invalid={errors.confirm?.message ? true : false}
                  {...field}
                />
              )}
            />
          </FormItem>

          <FormItem error={errors.referralCode?.message}>
            <Controller
              name="referralCode"
              control={control}
              render={({ field }) => (
                <Input placeholder={t('referral_code')} autoComplete="off" {...field} />
              )}
            />
          </FormItem>

          <FormItem className="RegisterPage__FirstAction">
            <Button className="AuthCard__action" type="primary">
              {t('sign_up')}
            </Button>
          </FormItem>

          <FormItem>
            <Button className="AuthCard__action" type="default" onClick={handleSignInClick}>
              {t('already_have_an_account')}
            </Button>
          </FormItem>
        </form>
      </div>
    </div>
  );
});

export default RegisterPage;
